import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeliveryCompany } from "../../../entity/DeliveryCompany";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Image,
  Spinner,
  useColorModeValue,
  Flex,
  VStack,
  Heading,
  Text,
  IconButton,
  HStack,
  Button,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogCloseButton,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { format } from "date-fns";
import { COLORS } from "../../../utils/Colors";
import { CreateUpdateDeliveryCompany } from "./components/CreateUpdateDeliveryCompany";
import { WSDeliveryInstance } from "../../../services/remote/delivery/WSDelivery";
import { IoIosMore } from "react-icons/io";
import "./DeliveryManager.css";
import { useNavigate } from "react-router-dom";
import { VRoutes } from "../../../navigation/Routes";
import { useDispatch } from "react-redux";
import { setDeliveryCompany } from "../../../store/slices/DeliverySlice";
import { CONSTANTS } from "../../../utils/Constants";

const options = [
  { code: CONSTANTS.GO_TO_COLLECTION, label: "deliveryConfig.addRequest" },
  {
    code: CONSTANTS.ADD_COLLECTION_TO_ACTION,
    label: "deliveryConfig.addCollectionToAction",
  },
];

export const DeliveryManager: React.FC = () => {
  const { t } = useTranslation();
  const [deliveryCompanies, setDeliveryCompanies] = useState<DeliveryCompany[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] =
    useState<DeliveryCompany | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] =
    useState<DeliveryCompany | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    WSDeliveryInstance.getDeliveryCompanies()
      .then((deliveryCompanies) => {
        setDeliveryCompanies(deliveryCompanies);
      })
      .catch((error) => {
        console.error(t("delivery.FailedToFetch"), error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [t]);

  const handleSave = async (company: DeliveryCompany) => {
    try {
      if (company.id) {
        // Update existing company
        await WSDeliveryInstance.updateDeliveryCompany(company);

        setDeliveryCompanies((prev) =>
          prev.map((c) => (c.id === company.id ? company : c)),
        );

        toast({
          title: t("delivery.companyUpdated") || "Company updated",
          description:
            t("delivery.companyUpdatedMessage") ||
            "The delivery company has been updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new company
        await WSDeliveryInstance.createDeliveryCompany(company);

        // Refresh the list
        const updatedCompanies =
          await WSDeliveryInstance.getDeliveryCompanies();
        setDeliveryCompanies(updatedCompanies);

        toast({
          title: t("delivery.companyCreated") || "Company created",
          description:
            t("delivery.companyCreatedMessage") ||
            "The delivery company has been created successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      // Close drawer and reset
      setIsDrawerOpen(false);
      setSelectedCompany(null);
    } catch (error) {
      toast({
        title: t("delivery.saveFailed") || "Save failed",
        description:
          t("delivery.saveFailedMessage") ||
          "Failed to save the delivery company.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = (company: DeliveryCompany) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (companyToDelete?.id) {
      try {
        await WSDeliveryInstance.deleteDeliveryCompany(companyToDelete.id);

        // Update local state to remove the deleted company
        setDeliveryCompanies((prev) =>
          prev.filter((company) => company.id !== companyToDelete.id),
        );

        toast({
          title: t("delivery.companyDeleted") || "Company deleted",
          description:
            t("delivery.companyDeletedMessage") ||
            "The delivery company has been deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: t("delivery.deleteFailed") || "Delete failed",
          description:
            t("delivery.deleteFailedMessage") ||
            "Failed to delete the delivery company.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setDeleteDialogOpen(false);
        setCompanyToDelete(null);
      }
    }
  };

  const handleEdit = (company: DeliveryCompany) => {
    setSelectedCompany(company);
    setIsDrawerOpen(true);
  };

  const handleUpdate = async (company: DeliveryCompany) => {
    if (company.id) {
      try {
        await WSDeliveryInstance.updateDeliveryCompany(company);

        // Update local state
        setDeliveryCompanies((prev) =>
          prev.map((c) => (c.id === company.id ? company : c)),
        );

        toast({
          title: t("delivery.companyUpdated") || "Company updated",
          description:
            t("delivery.companyUpdatedMessage") ||
            "The delivery company has been updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Close drawer and reset selected company
        setIsDrawerOpen(false);
        setSelectedCompany(null);
      } catch (error) {
        toast({
          title: t("delivery.updateFailed") || "Update failed",
          description:
            t("delivery.updateFailedMessage") ||
            "Failed to update the delivery company.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const tableBg = useColorModeValue("white", "gray.800");
  const headerBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" thickness="4px" color="blue.500" />
      </Flex>
    );
  }

  const handleSelect = async (code: string, companyId: string) => {
    switch (code) {
      case CONSTANTS.GO_TO_COLLECTION:
        dispatch(
          setDeliveryCompany(
            deliveryCompanies.filter(
              (deliveryCompany) => deliveryCompany.id === companyId,
            )[0],
          ),
        );
        navigate(VRoutes.DASHBOARD + VRoutes.DELIVERY_COLLECTIONS + companyId);
        break;
      case CONSTANTS.ADD_COLLECTION_TO_ACTION:
        navigate(
          VRoutes.DASHBOARD +
            VRoutes.DELIVERY_ADD_COLLECTION_TO_ACTION +
            companyId,
        );
        break;
    }
  };
  return (
    <Box p={6}>
      <HStack justify="space-between" align="center">
        <VStack align="start" spacing={4} mb={8}>
          <Heading size="lg" fontWeight="semibold">
            {t("delivery.deliveryCompanies") || "Delivery Companies"}
          </Heading>
          <Text color="gray.600" fontSize="sm">
            {t("delivery.manageDeliveryCompanies") ||
              "Manage your delivery company integrations"}
          </Text>
        </VStack>
        <Button onClick={() => setIsDrawerOpen(true)}>
          {t("delivery.addDeliveryCompany")}
        </Button>
      </HStack>

      <Box
        bg={tableBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
        shadow="md"
      >
        <Table variant="simple">
          <Thead bg={headerBg}>
            <Tr>
              <Th width="80px">{t("logo")}</Th>
              <Th>{t("companyName")}</Th>
              <Th width="120px">{t("status")}</Th>
              <Th width="150px">{t("createdDate")}</Th>
              <Th width="150px">{t("updatedDate")}</Th>
              <Th width="120px">{t("actions")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {deliveryCompanies.map((company) => (
              <Tr
                key={company.id}
                _hover={{ bg: "gray.50", _dark: "gray.700" }}
                transition="background-color 0.2s"
              >
                <Td>
                  <Box
                    w="50px"
                    h="50px"
                    bg="gray.50"
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    {company.logo ? (
                      <Image
                        src={company.logo}
                        alt={company.name}
                        maxH="100%"
                        maxW="100%"
                        objectFit="contain"
                        fallbackSrc="https://via.placeholder.com/50x50?text=Logo"
                      />
                    ) : (
                      <Box
                        w="30px"
                        h="30px"
                        bg="blue.100"
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text fontSize="sm" fontWeight="bold" color="blue.600">
                          {company.name.charAt(0).toUpperCase()}
                        </Text>
                      </Box>
                    )}
                  </Box>
                </Td>

                <Td>
                  <Text
                    fontWeight="semibold"
                    fontSize="md"
                    color={COLORS.Text.Primary}
                  >
                    {company.name}
                  </Text>
                </Td>

                <Td>
                  <Badge
                    colorScheme={company.isActive ? "green" : "red"}
                    variant="solid"
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="full"
                    textTransform="capitalize"
                  >
                    {company.isActive
                      ? t("delivery.active") || "Active"
                      : t("delivery.inactive") || "Inactive"}
                  </Badge>
                </Td>

                <Td>
                  <Text fontSize="sm" color="gray.600">
                    {company.createdAt
                      ? format(new Date(company.createdAt), "MMM dd, yyyy")
                      : "N/A"}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {company.createdAt
                      ? format(new Date(company.createdAt), "HH:mm")
                      : "N/A"}
                  </Text>
                </Td>

                <Td>
                  <Text fontSize="sm" color="gray.600">
                    {company.updatedAt
                      ? format(new Date(company.updatedAt), "MMM dd, yyyy")
                      : "N/A"}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {company.updatedAt
                      ? format(new Date(company.updatedAt), "HH:mm")
                      : "N/A"}
                  </Text>
                </Td>

                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      aria-label="Edit company"
                      icon={<EditIcon />}
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      onClick={() => handleEdit(company)}
                    />
                    <IconButton
                      aria-label="Delete company"
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      onClick={() => handleDelete(company)}
                    />

                    <Menu>
                      <MenuButton
                        as={Text}
                        _hover={{ backgroundColor: "transparent" }}
                        backgroundColor="transparent"
                        className="btn"
                      >
                        <IoIosMore color="#4a5568" />
                      </MenuButton>
                      <MenuList>
                        {options.map((l) => (
                          <MenuItem
                            key={l.code}
                            onClick={() => handleSelect(l.code, company.id!!)}
                            className="morebtn"
                          >
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              textColor={COLORS.Text.Primary}
                            >
                              {t(l.label)}
                            </Text>
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {deliveryCompanies.length === 0 && (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={16}
            bg="gray.50"
          >
            <Text fontSize="lg" fontWeight="semibold" color="gray.700" mb={2}>
              {t("delivery.NoDeliveryCompaniesAvailable") ||
                "No delivery companies available"}
            </Text>
            <Text color="gray.500" textAlign="center">
              {t("delivery.NoCompaniesMessage") ||
                "There are no delivery companies configured yet."}
            </Text>
          </Flex>
        )}
      </Box>
      <CreateUpdateDeliveryCompany
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedCompany(null);
        }}
        company={selectedCompany}
        onSave={handleSave}
      />

      <AlertDialog
        isOpen={deleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("delivery.deleteCompany") || "Delete Delivery Company"}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t("delivery.deleteConfirmation") ||
                "Are you sure you want to delete this delivery company?"}
              <br />
              <strong>{companyToDelete?.name}</strong>
              <br />
              {t("delivery.deleteWarning") || "This action cannot be undone."}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setDeleteDialogOpen(false)}
              >
                {t("cancel") || "Cancel"}
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                {t("delete") || "Delete"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
